// src/components/GoogleLoginButton.jsx
import React, { useEffect, useRef, useState } from 'react';
import { GOOGLE_AUTH_CLIENT_ID } from '../config';
import '../styles/GoogleLoginButton.css';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

const loadGis = () =>
  new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();

    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', () => reject(new Error('GIS no disponible')));
      return;
    }

    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', resolve);
    script.addEventListener('error', () => reject(new Error('GIS no disponible')));
    document.head.appendChild(script);
  });

/**
 * Botón "Continuar con Google". Entrega al padre el id_token y el nonce con el
 * que se pidió, para que el backend pueda validarlos.
 */
const GoogleLoginButton = ({ onCredential, text = 'continue_with', divider = 'before', children }) => {
  const containerRef = useRef(null);
  const nonceRef = useRef('');
  const callbackRef = useRef(onCredential);
  const [error, setError] = useState('');

  callbackRef.current = onCredential;
  if (!nonceRef.current) {
    nonceRef.current = window.crypto.randomUUID();
  }

  useEffect(() => {
    let cancelled = false;

    loadGis()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_AUTH_CLIENT_ID,
          nonce: nonceRef.current,
          callback: response => callbackRef.current?.(response.credential, nonceRef.current),
        });
        window.google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          shape: 'rectangular',
          text,
          locale: 'es',
        });
      })
      .catch(() => setError('No se pudo cargar el inicio de sesión con Google.'));

    return () => {
      cancelled = true;
    };
  }, [text]);

  if (!GOOGLE_AUTH_CLIENT_ID) return null;

  return (
    <div className="google-login">
      {divider === 'before' && <div className="google-login__divider">o</div>}
      <div ref={containerRef} className="google-login__button" />
      {error && <p className="google-login__error">{error}</p>}
      {children && <p className="google-login__note">{children}</p>}
      {divider === 'after' && <div className="google-login__divider">o</div>}
    </div>
  );
};

export default GoogleLoginButton;
